import {useDataQuery, useRpcQuery} from '@tnc/shared';
import {useState} from 'react';

type MarketStats = {
  current: number;
  previous: number;
};

type SpeculatorDashboardMarketStatsT = {
  global: {
    active_positions: MarketStats;
    active_products: MarketStats;
    active_items: MarketStats;
  };
  personal: {
    active_positions: MarketStats;
    active_products: MarketStats;
    active_items: MarketStats;
  };
};

const MarketOverview = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [selectedMarket, setSelectedMarket] = useState({market_id: 1, market_name: 'California'});
  const [viewMode, setViewMode] = useState<'global' | 'personal'>('global');

  const {data: {data: markets} = {data: []}} = useDataQuery({
    table: 'Market',
    columns: ['market_id', 'market_name'],
    enabled: true,
  });

  const {
    data: {data: marketStats} = {data: null},
    error: marketStatsError,
    isLoading: marketStatsLoading,
  } = useRpcQuery<'get_speculator_dashboard_market_stats', {__market_id: number}>({
    functionName: 'get_speculator_dashboard_market_stats',
    functionArgs: {
      __market_id: selectedMarket.market_id,
    },
    enabled: !!selectedMarket.market_id,
  });

  const filteredMarkets =
    query === '' ? markets : markets?.filter(market => market.market_name.toLowerCase().includes(query.toLowerCase()));

  const calculatePercentageChange = (stats: MarketStats): number => {
    if (!stats.previous) return 0;
    return ((stats.current - stats.previous) / stats.previous) * 100;
  };

  const currentStats = marketStats
    ? {
        marketVolume:
          viewMode === 'global'
            ? `$${(marketStats as SpeculatorDashboardMarketStatsT).global?.active_items.current?.toLocaleString() ?? 0}`
            : `$${(marketStats as SpeculatorDashboardMarketStatsT).personal?.active_items.current?.toLocaleString() ?? 0}`,
        marketVolumeChange: calculatePercentageChange(
          viewMode === 'global'
            ? (marketStats as SpeculatorDashboardMarketStatsT).global.active_items
            : (marketStats as SpeculatorDashboardMarketStatsT).personal.active_items
        ),
        activeProducts:
          viewMode === 'global'
            ? ((marketStats as SpeculatorDashboardMarketStatsT).global?.active_products.current ?? 0)
            : ((marketStats as SpeculatorDashboardMarketStatsT).personal?.active_products.current ?? 0),
        activeProductsChange: calculatePercentageChange(
          viewMode === 'global'
            ? (marketStats as SpeculatorDashboardMarketStatsT).global.active_products
            : (marketStats as SpeculatorDashboardMarketStatsT).personal.active_products
        ),
        avgPositionSize:
          viewMode === 'global'
            ? `$${Math.round(((marketStats as SpeculatorDashboardMarketStatsT).global.active_items.current ?? 0) / ((marketStats as SpeculatorDashboardMarketStatsT).global.active_positions.current || 1)).toLocaleString()}`
            : `$${Math.round(((marketStats as SpeculatorDashboardMarketStatsT).personal.active_items.current ?? 0) / ((marketStats as SpeculatorDashboardMarketStatsT).personal.active_positions.current || 1)).toLocaleString()}`,
        avgPositionSizeChange: calculatePercentageChange(
          viewMode === 'global'
            ? {
                current:
                  (marketStats as SpeculatorDashboardMarketStatsT).global.active_items.current /
                  ((marketStats as SpeculatorDashboardMarketStatsT).global.active_positions.current || 1),
                previous:
                  (marketStats as SpeculatorDashboardMarketStatsT).global.active_items.previous /
                  ((marketStats as SpeculatorDashboardMarketStatsT).global.active_positions.previous || 1),
              }
            : {
                current:
                  (marketStats as SpeculatorDashboardMarketStatsT).personal.active_items.current /
                  ((marketStats as SpeculatorDashboardMarketStatsT).personal.active_positions.current || 1),
                previous:
                  (marketStats as SpeculatorDashboardMarketStatsT).personal.active_items.previous /
                  ((marketStats as SpeculatorDashboardMarketStatsT).personal.active_positions.previous || 1),
              }
        ),
      }
    : {
        marketVolume: '$0',
        marketVolumeChange: 0,
        activeProducts: 0,
        activeProductsChange: 0,
        avgPositionSize: '$0',
        avgPositionSizeChange: 0,
      };

  return (
    <div className="box h-full">
      <div className="box-header flex items-center justify-between">
        <div className="flex items-center gap-4">
          <h5 className="box-title">Market Overview (30 days)</h5>
          <div className="flex rounded-md bg-gray-100 p-1 dark:bg-gray-800">
            <button
              onClick={() => setViewMode('global')}
              className={`rounded px-3 py-1 text-sm transition-colors ${
                viewMode === 'global'
                  ? 'bg-white text-gray-800 shadow dark:bg-gray-700 dark:text-white'
                  : 'text-gray-600 hover:text-gray-800 dark:text-gray-300 dark:hover:text-white'
              }`}
            >
              Global
            </button>
            <button
              onClick={() => setViewMode('personal')}
              className={`rounded px-3 py-1 text-sm transition-colors ${
                viewMode === 'personal'
                  ? 'bg-white text-gray-800 shadow dark:bg-gray-700 dark:text-white'
                  : 'text-gray-600 hover:text-gray-800 dark:text-gray-300 dark:hover:text-white'
              }`}
            >
              My Stats
            </button>
          </div>
        </div>
        <div className="relative w-72">
          <div className="relative">
            <input
              type="text"
              className="form-control pe-10"
              placeholder="Search market..."
              value={query}
              onChange={e => setQuery(e.target.value)}
              onFocus={() => setIsOpen(true)}
            />
            <button className="absolute inset-y-0 right-0 flex items-center px-3" onClick={() => setIsOpen(!isOpen)}>
              <i className="ri-arrow-down-s-line text-lg"></i>
            </button>
          </div>

          {isOpen && (
            <div className="dark:bg-bgdark absolute mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 shadow-lg">
              {filteredMarkets.length === 0 ? (
                <div className="px-4 py-2 text-gray-700 dark:text-gray-300">Nothing found.</div>
              ) : (
                filteredMarkets.map(market => (
                  <div
                    key={market.market_id}
                    className="cursor-pointer px-4 py-2 hover:bg-primary hover:text-white dark:text-gray-300"
                    onClick={() => {
                      setSelectedMarket(market);
                      setIsOpen(false);
                      setQuery('');
                    }}
                  >
                    <div className="flex items-center">
                      {selectedMarket.market_id === market.market_id && <i className="ri-check-line me-2"></i>}
                      <span>{market.market_name}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>
      <div className="box-body">
        {marketStatsLoading ? (
          <div className="flex justify-center py-8">
            <div className="spinner"></div>
          </div>
        ) : marketStatsError ? (
          <div className="text-red-500 text-center">Error loading market stats</div>
        ) : (
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center">
              <p className="mb-1 text-[#8c9097] dark:text-white/50">Market Volume</p>
              <h3 className="mb-0 font-semibold">{currentStats.marketVolume}</h3>
              <span className={`text-sm ${currentStats.marketVolumeChange >= 0 ? 'text-success' : 'text-danger'}`}>
                <i className={`ri-arrow-${currentStats.marketVolumeChange >= 0 ? 'up' : 'down'}-s-line`}></i>{' '}
                {Math.abs(currentStats.marketVolumeChange)}%
              </span>
            </div>
            <div className="text-center">
              <p className="mb-1 text-[#8c9097] dark:text-white/50">Active Products</p>
              <h3 className="mb-0 font-semibold">{currentStats.activeProducts}</h3>
              <span className={`text-sm ${currentStats.activeProductsChange >= 0 ? 'text-success' : 'text-danger'}`}>
                <i className={`ri-arrow-${currentStats.activeProductsChange >= 0 ? 'up' : 'down'}-s-line`}></i>{' '}
                {Math.abs(currentStats.activeProductsChange)}%
              </span>
            </div>
            <div className="text-center">
              <p className="mb-1 text-[#8c9097] dark:text-white/50">Avg. Position Size</p>
              <h3 className="mb-0 font-semibold">{currentStats.avgPositionSize}</h3>
              <span className={`text-sm ${currentStats.avgPositionSizeChange >= 0 ? 'text-success' : 'text-danger'}`}>
                <i className={`ri-arrow-${currentStats.avgPositionSizeChange >= 0 ? 'up' : 'down'}-s-line`}></i>{' '}
                {Math.abs(currentStats.avgPositionSizeChange)}%
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MarketOverview;
